import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_ITEM = { code: "", title: "", menuPlacement: "", content: "", paragraphs: [] };
const newParagraph = () => ({ _tempId: crypto.randomUUID(), headingLevel: "h2", heading: "", body: "" });

function ParagraphEditor({ paragraphs, onChange }) {
  const add = () => onChange([...paragraphs, { ...newParagraph(), order: paragraphs.length }]);

  const remove = (i) => onChange(paragraphs.filter((_, idx) => idx !== i));

  const update = (i, field, value) => {
    const next = paragraphs.map((p, idx) => idx === i ? { ...p, [field]: value } : p);
    onChange(next);
  };

  const moveUp = (i) => {
    if (i === 0) return;
    const next = [...paragraphs];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  };

  const moveDown = (i) => {
    if (i === paragraphs.length - 1) return;
    const next = [...paragraphs];
    [next[i], next[i + 1]] = [next[i + 1], next[i]];
    onChange(next);
  };

  return (
    <div className="paragraph-editor">
      {paragraphs.map((p, i) => (
        <div key={p._id || p._tempId} className="paragraph-item">
          <div className="paragraph-controls">
            <button type="button" onClick={() => moveUp(i)} disabled={i === 0} title="Omhoog">↑</button>
            <button type="button" onClick={() => moveDown(i)} disabled={i === paragraphs.length - 1} title="Omlaag">↓</button>
            <button type="button" className="btn-danger" onClick={() => remove(i)} title="Verwijderen">×</button>
          </div>
          <div className="paragraph-row">
            <select
              value={p.headingLevel}
              onChange={e => update(i, "headingLevel", e.target.value)}
              className="paragraph-level"
            >
              <option value="h2">H2</option>
              <option value="h3">H3</option>
            </select>
            <input
              value={p.heading}
              onChange={e => update(i, "heading", e.target.value)}
              placeholder="Koptekst"
              className="paragraph-heading"
            />
          </div>
          <textarea
            value={p.body}
            onChange={e => update(i, "body", e.target.value)}
            placeholder="Inhoud (HTML toegestaan)"
            className="paragraph-body"
          />
        </div>
      ))}
      <button type="button" onClick={add} className="btn-secondary">+ Paragraaf toevoegen</button>
    </div>
  );
}

function ContentEditor({ item, csrfToken, onSaved, onDeleted, onCancel }) {
  const [form, setForm] = useState(item);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const isNew = !item._id;

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const save = async () => {
    setSaving(true);
    setError("");
    const paragraphs = form.paragraphs.map((p, i) => ({ ...p, order: i }));
    const body = JSON.stringify({ ...form, paragraphs });

    const res = await fetch(
      isNew ? `${API}/admin/content` : `${API}/admin/content/${item._id}`,
      {
        method: isNew ? "POST" : "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", "CSRF-Token": csrfToken },
        body
      }
    );
    setSaving(false);
    if (!res.ok) {
      const data = await res.json();
      setError(data.error || "Opslaan mislukt");
      return;
    }
    const saved = await res.json();
    onSaved(saved, isNew);
  };

  const remove = async () => {
    if (!window.confirm(`Pagina "${form.code}" verwijderen?`)) return;
    await fetch(`${API}/admin/content/${item._id}`, {
      method: "DELETE",
      credentials: "include",
      headers: { "CSRF-Token": csrfToken }
    });
    onDeleted(item._id);
  };

  return (
    <div className="content-editor">
      <div className="content-editor-meta">
        <div className="content-editor-field">
          <label>Code (unieke sleutel, URL-segment)</label>
          <input value={form.code} onChange={e => set("code", e.target.value)} placeholder="bijv. over-ons" />
        </div>
        <div className="content-editor-field">
          <label>Paginatitel</label>
          <input value={form.title} onChange={e => set("title", e.target.value)} placeholder="Bijv. Over ons" />
        </div>
        <div className="content-editor-field">
          <label>Menupositie</label>
          <input value={form.menuPlacement} onChange={e => set("menuPlacement", e.target.value)} placeholder="bijv. top-menu (leeg = niet in menu)" />
        </div>
      </div>

      <label>Hoofdinhoud (HTML)</label>
      <textarea
        className="content-main-body"
        value={form.content}
        onChange={e => set("content", e.target.value)}
        placeholder="Vrije HTML-inhoud boven de paragrafen"
      />

      <label>Paragrafen</label>
      <ParagraphEditor
        paragraphs={form.paragraphs}
        onChange={paras => set("paragraphs", paras)}
      />

      {error && <p className="content-error">{error}</p>}

      <div className="content-editor-actions">
        <button onClick={save} disabled={saving}>{saving ? "Opslaan…" : "Opslaan"}</button>
        <button className="btn-secondary" onClick={onCancel}>Annuleren</button>
        {!isNew && (
          <button className="btn-danger" onClick={remove}>Verwijderen</button>
        )}
      </div>
    </div>
  );
}

export default function AdminContent({ csrfToken }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null = list, object = editor

  useEffect(() => {
    fetch(`${API}/admin/content`, { credentials: "include" })
      .then(r => r.json())
      .then(setItems);
  }, []);

  const handleSaved = (saved, isNew) => {
    if (isNew) {
      setItems(prev => [...prev, saved].sort((a, b) => a.code.localeCompare(b.code)));
    } else {
      setItems(prev => prev.map(i => i._id === saved._id ? saved : i));
    }
    setEditing(null);
  };

  const handleDeleted = (id) => {
    setItems(prev => prev.filter(i => i._id !== id));
    setEditing(null);
  };

  if (editing !== null) {
    return (
      <ContentEditor
        item={editing}
        csrfToken={csrfToken}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div>
      <div className="content-list-header">
        <h2>Pagina&apos;s</h2>
        <button onClick={() => setEditing({ ...EMPTY_ITEM })}>+ Nieuwe pagina</button>
      </div>
      <div className="content-list">
        {items.length === 0 && <p>Nog geen content-pagina&apos;s.</p>}
        {items.map(item => (
          <div key={item._id} className="content-list-item">
            <span>
              <code>{item.code}</code> — {item.title}
              {item.menuPlacement && <small> ({item.menuPlacement})</small>}
            </span>
            <button className="btn-secondary" onClick={() => setEditing(item)}>Bewerken</button>
          </div>
        ))}
      </div>
    </div>
  );
}
